import * as Sentry from '@sentry/react';
import React from 'react';
import { useDispatch } from 'react-redux';

import { useMeQuery } from '../components/hooks/useMeQuery';
import { AuthenticatedRouter } from '../routes/AuthenticatedRouter';
import type { UserDto } from '../types/users/dto/user.dto';

import { ConsoleLayoutLoading } from './layouts/ConsoleLayoutLoading';

// other
import { fetchP100Coverages } from '../accounts/AccountService';
import { getProfilePhoto } from '../api/ImageService';
import { useAnalytics } from '../config/analytics/useAnalytics';
import { setAuthentication } from '../reducers/AuthReducers';
import { setGlobalTeam } from '../reducers/team.reducer';
import { setUserAccount } from '../store/actions/user-account.store.actions';
import {
  setInitialUser,
  setUserAvatar,
} from '../store/actions/user.store.actions';
import { ALL_TEAM } from '../teams/TeamsMenu';
import { PERSONA_TYPES } from './_statics/persona.statics';

const USER_CACHE_STALE_MINUTES = 10;

/**
 * @name ConsoleResolver
 * @description The user is logged in by the time they reach this component. Retrieve all user and account dependencies and determine where to send them next.
 */
export function ConsoleResolver() {
  const dispatch = useDispatch();
  const analytics = useAnalytics({ skipPersona: true });

  /* Temporary to support older P100 Features relying on coverages being in redux */
  React.useEffect(() => {
    fetchP100Coverages().then((resp) => {
      dispatch(setAuthentication({ P_100: resp.data }));
    });
    // eslint-disable-next-line
  }, []);

  // `handleSuccessfulMeResponse` fires on every /me success, including
  // refetches, and each one kicks off its own independent `getProfilePhoto`
  // call. Without this, an older request that happens to resolve after a
  // newer one (out-of-order network timing) could overwrite a fresh avatar
  // with stale data, or dispatch into the store after this component has
  // unmounted (e.g. on logout). `photoRequestIdRef` lets a `.then`/`.catch`
  // detect it's been superseded and bail instead of dispatching.
  const photoRequestIdRef = React.useRef(0);
  const isMountedRef = React.useRef(true);
  React.useEffect(() => {
    // Reset on every effect run, not just cleanup, so Strict Mode's
    // dev-only mount/cleanup/remount cycle doesn't leave this permanently
    // false.
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  function handleSuccessfulMeResponse(nextUser: UserDto) {
    const { account, ...user } = nextUser;

    /* todo: make sure there is a user, if not set an error to the store */
    // Hydrate the user/account immediately - name, email, phone, etc. should
    // never wait on the avatar. Deliberately omit avatarUrl here: this runs
    // on every successful /me fetch, including refetches (e.g. after a
    // profile save invalidates the query), not just the initial app load.
    // Forcing avatarUrl to null on every dispatch would flicker/clear an
    // already-loaded avatar each time /me refetches. The avatar is fetched
    // separately below and patched in via SET_USER_AVATAR whenever it
    // resolves, so a slow/failing photo endpoint can't delay or block the
    // rest of the app from having a populated actor.
    dispatch(setInitialUser({ ...user }));
    dispatch(setUserAccount(account));

    const requestId = ++photoRequestIdRef.current;
    const isStale = () =>
      !isMountedRef.current || requestId !== photoRequestIdRef.current;

    getProfilePhoto()
      .then((profilePhotoResponse) => {
        if (isStale()) {
          // Superseded by a newer /me success, or the component unmounted
          // (e.g. logout) before this resolved - discard it.
          return;
        }
        if (profilePhotoResponse.status === 200) {
          dispatch(setUserAvatar(profilePhotoResponse.data));
        } else if (profilePhotoResponse.status === 204) {
          // No photo exists (e.g. it was removed) - explicitly clear any
          // previously-stored avatar rather than leaving a stale one.
          dispatch(setUserAvatar(null));
        }
      })
      .catch(() => {
        // If the photo fetch fails, leave whatever avatar (if any) is
        // already in the store untouched rather than clearing it - a
        // transient failure on a /me refetch shouldn't blank out an
        // already-displayed avatar.
      });

    // * ensures that no team narrowing occurs while in CB persona. Without this, a team selection made after jumping in to an agency could (sneakily) alter behavior on CB persona.
    if (account.accountType === PERSONA_TYPES.COWBELL) {
      dispatch(setGlobalTeam(ALL_TEAM));
    }

    if (user?.id) {
      analytics.track('test.pre_identify', {
        uid: user.id,
        location: 'ConsoleResolver',
      });

      analytics.identify(user.id, {
        name: user.firstName + ' ' + user.lastName,
        email: user.email,
      });

      // set sentry session info
      Sentry.setUser({ id: user.id });
      Sentry.setContext('user', { persona: account?.accountType });
    }
  }

  const {
    data: me,
    isLoading: isLoadingMe,
    isError: isMeError,
  } = useMeQuery({
    staleTime: 60000 * USER_CACHE_STALE_MINUTES,
    refetchOnWindowFocus: false,
    onSuccess: handleSuccessfulMeResponse,
  });

  if (isLoadingMe) {
    return <ConsoleLayoutLoading />;
  }

  if (isMeError) {
    return (
      <ConsoleLayoutLoading message="An error occurred and we couldn't recover, try logging out and back in. If the issue continues contact support." />
    );
  }

  return <AuthenticatedRouter user={me} />;
}
